import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn } from './ui/kit';

/**
 * Who did it, where they were, and when.
 *
 * Reads are not here. This is the trail of CHANGES -- what became what -- plus
 * the requests that attempted one, including the ones that were refused. Five
 * refusals in a minute is a person trying doors, and that is worth seeing even
 * though nothing changed.
 */

async function req(url) {
  const res = await authedFetch(url);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
}

const inputStyle = {
  padding: '8px 11px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13,
};
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11.5,
             color: color.textSecondary, fontWeight: 600, whiteSpace: 'nowrap',
             borderBottom: `1px solid ${color.border}` };
const td = { padding: '8px 10px', fontSize: 12.5, verticalAlign: 'top',
             borderBottom: `1px solid ${color.border}` };

const OP_TONE = { INSERT: '#27ae60', UPDATE: '#f39c12', DELETE: '#e74c3c' };

function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function short(v) {
  if (v === null || v === undefined) return '(empty)';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
}

/** Where the person was, or the honest reason there is no answer. */
function Where({ row }) {
  if (row.gps_lat != null && row.gps_lng != null) {
    const label = `${Number(row.gps_lat).toFixed(5)}, ${Number(row.gps_lng).toFixed(5)}`;
    return (
      <>
        <a href={`https://www.google.com/maps?q=${row.gps_lat},${row.gps_lng}`}
           target="_blank" rel="noreferrer"
           style={{ color: color.royal, fontSize: 12 }}>{label}</a>
        {row.gps_accuracy_m != null && (
          <div style={{ fontSize: 11, color: color.textMuted }}>
            ±{Math.round(row.gps_accuracy_m)}m
          </div>)}
      </>
    );
  }
  // Each of these means something different and none of them means "blank".
  const reason = {
    denied: 'location refused',
    unavailable: 'location unavailable',
    timeout: 'location timed out',
    unsupported: 'device cannot',
    'insecure-origin': 'not over HTTPS',
    'out-of-range': 'device sent nonsense',
    malformed: 'device sent nonsense',
  }[row.gps_status];
  return (
    <span style={{ fontSize: 11.5, color: color.textMuted }}>
      {reason || 'not captured'}
    </span>
  );
}

function Changes({ changed }) {
  const fields = Object.entries(changed || {});
  if (!fields.length) return <span style={{ color: color.textMuted }}>—</span>;
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      {fields.slice(0, 6).map(([field, v]) => (
        <div key={field} style={{ fontSize: 12 }}>
          <span style={{ color: color.textSecondary }}>{field}</span>{' '}
          <span style={{ color: color.textMuted }}>{short(v.from)}</span>
          <span style={{ color: color.textMuted }}> → </span>
          <strong>{short(v.to)}</strong>
        </div>
      ))}
      {fields.length > 6 && (
        <div style={{ fontSize: 11, color: color.textMuted }}>
          + {fields.length - 6} more field{fields.length - 6 === 1 ? '' : 's'}
        </div>)}
    </div>
  );
}

export default function AuditTrail({ notify }) {
  const [tab, setTab] = useState('changes');
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [cover, setCover] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [operation, setOperation] = useState('');
  const [table, setTable] = useState('');
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = new URLSearchParams({ days, limit: 100, offset });
      if (search.trim()) q.set('search', search.trim());
      if (operation) q.set('operation', operation);
      if (table) q.set('table', table);
      const [changes, ev, cv] = await Promise.all([
        req(`/api/audit/changes?${q}`),
        req(`/api/audit/events?days=${days}&limit=100`),
        req('/api/audit/coverage'),
      ]);
      setData(changes); setEvents(ev); setCover(cv);
    } catch (e) { setError(e.message); }
  }, [days, search, operation, table, offset]);

  useEffect(() => { load(); }, [load]);

  const tables = useMemo(
    () => (cover?.audited || []).slice().sort(), [cover]);

  return (
    <div>
      {error && <Banner tone="danger" title="Could not load">{error}</Banner>}

      {cover && (
        <Banner tone="info" title={`${cover.audited_count} of ${cover.total_tables} tables audited`}>
          {cover.totals?.changes?.toLocaleString() || 0} changes recorded
          {cover.totals?.since && ` since ${when(cover.totals.since)}`}.
          {cover.totals?.outside_app > 0 && (
            <strong> {cover.totals.outside_app} were made outside the app
              (direct database access).</strong>)}
          {' '}The trail is append-only: it cannot be edited or deleted, by
          anyone, including an administrator.
        </Banner>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                    marginBottom: space(2), alignItems: 'center' }}>
        {[['changes', 'Changes'], ['events', 'Actions'],
          ['coverage', 'What is covered']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  style={{ padding: '7px 14px', borderRadius: radius.sm,
                           fontSize: 13, cursor: 'pointer',
                           border: `1px solid ${tab === k ? color.royal : color.border}`,
                           background: tab === k ? color.royal : 'transparent',
                           color: tab === k ? '#fff' : color.text }}>
            {label}
          </button>
        ))}
        <select style={inputStyle} value={days}
                onChange={(e) => { setOffset(0); setDays(Number(e.target.value)); }}>
          {[1, 7, 30, 90, 365].map(d => (
            <option key={d} value={d}>
              {d === 1 ? 'Last 24 hours' : `Last ${d} days`}
            </option>))}
        </select>
        <Btn size="sm" variant="secondary" onClick={load}>Refresh</Btn>
      </div>

      {tab === 'changes' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                        marginBottom: space(1.5) }}>
            <input style={{ ...inputStyle, minWidth: 220 }} value={search}
                   placeholder="Search person, table or value"
                   onChange={(e) => { setOffset(0); setSearch(e.target.value); }} />
            <select style={inputStyle} value={operation}
                    onChange={(e) => { setOffset(0); setOperation(e.target.value); }}>
              <option value="">Every kind of change</option>
              <option value="INSERT">Created</option>
              <option value="UPDATE">Changed</option>
              <option value="DELETE">Deleted</option>
            </select>
            <select style={inputStyle} value={table}
                    onChange={(e) => { setOffset(0); setTable(e.target.value); }}>
              <option value="">Everywhere</option>
              {tables.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {data && (
            <>
              <div style={{ fontSize: 12, color: color.textSecondary,
                            marginBottom: space(1) }}>
                Showing {data.shown} of {data.total?.toLocaleString()} changes
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>When</th><th style={th}>Who</th>
                    <th style={th}>Did what</th><th style={th}>What changed</th>
                    <th style={th}>From where</th>
                  </tr></thead>
                  <tbody>
                    {data.items.map(r => (
                      <tr key={r.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(r.occurred_at)}</td>
                        <td style={td}>
                          {r.user_name || (
                            <span style={{ color: color.danger, fontSize: 12 }}>
                              {r.source === 'direct-database'
                                ? 'outside the app' : 'not signed in'}
                            </span>)}
                        </td>
                        <td style={td}>
                          <span style={{ color: OP_TONE[r.operation],
                                         fontWeight: 600, fontSize: 11.5 }}>
                            {r.operation}
                          </span>
                          <div style={{ fontSize: 11.5, color: color.textSecondary }}>
                            {r.table_name}
                          </div>
                        </td>
                        <td style={td}><Changes changed={r.changed} /></td>
                        <td style={td}>
                          <div style={{ fontSize: 12 }}>{r.ip_address || '—'}</div>
                          <Where row={r} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.items.length === 0 && (
                <p style={{ color: color.textSecondary }}>
                  Nothing changed in this period that matches.
                </p>)}
              <div style={{ display: 'flex', gap: 8, marginTop: space(1.5) }}>
                <Btn size="sm" variant="secondary" disabled={offset === 0}
                     onClick={() => setOffset(Math.max(0, offset - 100))}>
                  Newer
                </Btn>
                <Btn size="sm" variant="secondary"
                     disabled={offset + 100 >= (data.total || 0)}
                     onClick={() => setOffset(offset + 100)}>
                  Older
                </Btn>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'events' && events && (
        <>
          <Banner tone="info" title="Every attempt, including the refused ones">
            A request that was blocked changes nothing and so appears in no
            change list. It is often the row worth reading.
          </Banner>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>When</th><th style={th}>Who</th>
                <th style={th}>Action</th><th style={th}>Result</th>
                <th style={th}>Changed</th><th style={th}>From where</th>
              </tr></thead>
              <tbody>
                {events.items.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(r.occurred_at)}</td>
                    <td style={td}>
                      {r.user_name || <span style={{ color: color.danger }}>not signed in</span>}
                      {r.user_role && (
                        <div style={{ fontSize: 11, color: color.textMuted }}>
                          {r.user_role}
                        </div>)}
                    </td>
                    <td style={td}>
                      <strong style={{ fontSize: 11.5 }}>{r.method}</strong>{' '}
                      <span style={{ fontSize: 12 }}>{r.route || r.path}</span>
                    </td>
                    <td style={td}>
                      <span style={{ fontWeight: 600, fontSize: 12,
                                     color: r.status_code >= 400 ? color.danger : color.success }}>
                        {r.status_code}
                      </span>
                      <div style={{ fontSize: 11, color: color.textMuted }}>
                        {r.duration_ms}ms
                      </div>
                    </td>
                    <td style={td}>
                      {r.change_count > 0
                        ? `${r.change_count} record${r.change_count === 1 ? '' : 's'}`
                        : <span style={{ color: color.textMuted }}>nothing</span>}
                    </td>
                    <td style={td}>
                      <div style={{ fontSize: 12 }}>{r.ip_address || '—'}</div>
                      <Where row={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'coverage' && cover && (
        <>
          <Banner tone="info" title="Read from the running database">
            {cover.note}
          </Banner>
          <div style={{ display: 'grid', gap: space(2),
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <div>
              <h4>Audited ({cover.audited.length})</h4>
              <div style={{ fontSize: 12.5, lineHeight: 1.8 }}>
                {cover.audited.join(', ')}
              </div>
            </div>
            <div>
              <h4>Not audited ({cover.not_audited.length})</h4>
              <div style={{ fontSize: 12.5, lineHeight: 1.8,
                            color: color.textSecondary }}>
                {cover.not_audited.length
                  ? cover.not_audited.join(', ')
                  : 'Nothing is left out.'}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
